import { useQueryClient } from '@tanstack/react-query';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import { useRef, useState } from 'react';
import { useSession } from '../SessionProvider';
import {
  ExportFormat,
  getReportExportQueueStatus,
  getReportExportResult,
  getReportExportResultShipList,
  startQueryingReportExportResult,
  startQueryingReportExportResultShipList,
} from './exportReports';
import { useTimeZoneContext } from '@ais-ui/utils';

interface UseCompleteReportExportFetchingProcessResult {
  startJob: (args: ReportExportJobArgs) => void;
  isLoading: boolean;
  isError: boolean;
  data: string | Array<any> | undefined;
  status: string | undefined;
}

interface ReportExportJobArgs {
  jobId: string;
  format: ExportFormat;
  centerLat: number;
  centerLon: number;
  zoomLevel: number;
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

export const useCompleteReportExportFetchingProcess =
  (): UseCompleteReportExportFetchingProcessResult => {
    const { timeZone } = useTimeZoneContext();
    const [queueId, setQueueId] = useState<string>('');
    const [isReportFetched, setIsReportFetched] = useState(false);
    const [customError, setCustomError] = useState<unknown | null>(null);
    const { dbSession } = useSession();
    const [currentFormat, setCurrentFormat] = useState<ExportFormat | null>(
      null
    );
    const queryClient = useQueryClient();
    const processedQueueIdsRef = useRef(new Set());
    const resultCacheRef = useRef(new Map());

    const resetExportData = () => {
      setQueueId('');
      setIsReportFetched(false);
      setCustomError(null);
      setCurrentFormat(null);

      processedQueueIdsRef.current.clear();
      resultCacheRef.current.clear();

      queryClient.removeQueries(['pollStatusReportExport']);
      queryClient.removeQueries(['fetchExportResults']);
    };

    const startReportExportJob = useMutation(
      async (args: ReportExportJobArgs) => {
        resetExportData();

        const {
          jobId,
          format,
          centerLat,
          centerLon,
          zoomLevel,
          minLat,
          minLon,
          maxLat,
          maxLon,
        } = args;
        setCurrentFormat(format);
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        if (format === 'OF_SHIPLIST') {
          const queueId = await startQueryingReportExportResultShipList(
            dbSession,
            jobId
          );
          return queueId;
        } else {
          const queueId = await startQueryingReportExportResult(
            dbSession,
            jobId,
            format,
            centerLat,
            centerLon,
            zoomLevel,
            minLat,
            minLon,
            maxLat,
            maxLon,
            timeZone
          );
          return queueId;
        }
      },
      {
        onSuccess: (queueId) => {
          setQueueId(queueId);
        },
      }
    );

    const pollStatus = useQuery(
      ['pollStatusReportExport', queueId],
      async () => {
        try {
          if (!dbSession) {
            throw new Error('dbSession is not defined.');
          }

          const statusData = await getReportExportQueueStatus(
            dbSession,
            queueId
          );

          return statusData;
        } catch (error) {
          setCustomError(error);
          throw error;
        }
      },
      {
        enabled: !!queueId && !processedQueueIdsRef.current.has(queueId),
        refetchInterval: (data) => {
          if (
            data?.status === 'QueueComplete' ||
            data?.status === 'QueueFailed'
          ) {
            if (data?.status === 'QueueFailed') {
              setCustomError(new Error('Queue failed'));
            }
            return false;
          }
          return 1000;
        },
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: false,
      }
    );

    // Fetch results
    const fetchExportUrl = useQuery(
      ['fetchExportResults', queueId],
      async () => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }
        // Check if we've already processed this queueId
        if (processedQueueIdsRef.current.has(queueId)) {
          return resultCacheRef.current.get(queueId);
        }

        // Fetch the result
        let result;
        if (currentFormat === 'OF_SHIPLIST') {
          result = await getReportExportResultShipList(dbSession, queueId);
        } else {
          result = await getReportExportResult(dbSession, queueId);
        }

        // Mark this queueId as processed to prevent duplicate fetches
        processedQueueIdsRef.current.add(queueId);
        resultCacheRef.current.set(queueId, result);

        return result;
      },
      {
        enabled:
          pollStatus.data?.status === 'QueueComplete' && !isReportFetched,
        onSuccess: () => {
          setIsReportFetched(true);
        },
      }
    );

    // Function to start the job
    const startJob = (args: ReportExportJobArgs) => {
      setIsReportFetched(false);
      startReportExportJob.mutate(args);
    };

    // Only consider polling as active when we have a queueId and are actively checking status
    const isActivelyPollingQueueStatus = !!queueId && pollStatus.isFetching;

    // Only consider result fetching when queue is complete and we haven't yet fetched the report
    const isRetrievingCompletedReportData =
      pollStatus.data?.status === 'QueueComplete' &&
      !isReportFetched &&
      fetchExportUrl.isFetching;

    return {
      startJob,
      isLoading:
        startReportExportJob.isLoading ||
        isActivelyPollingQueueStatus ||
        isRetrievingCompletedReportData,
      isError:
        startReportExportJob.isError ||
        pollStatus.isError ||
        fetchExportUrl.isError ||
        !!customError ||
        pollStatus.data?.status === 'QueueFailed',
      data: fetchExportUrl.data,
      status: pollStatus.data?.status,
    };
  };
